import { useEffect, useRef, useState } from "react";
import { AGENT_SKILL_CHAT_INSTRUCTION_MAX_CHARS } from "@common/constants/agentSkill";
import type { AgentSkill } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { Avatar } from "@design-system/Avatar/Avatar";
import { SlateEditor } from "@components/mainInterface/chat/slate/SlateEditor";
import {
    serializeSlateValue,
    slateToPlainText,
} from "@components/mainInterface/chat/slate/slateHelpers";
import type { SlateValue } from "@components/mainInterface/chat/slate/slateTypes";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { timeAgo } from "@helpers/date";
import {
    useAgentSkillRevisions,
    useChatEditAgentSkillMutation,
} from "@api/agentSkill.api";
import {
    AgentSkillChatItem,
    RejectedChatEdit,
    buildAgentSkillChatThread,
    describeRejectedChatEdit,
} from "./agentSkillChat";
import {
    AGENT_SKILL_MANAGE_FORBIDDEN_TOOLTIP,
    FLAGGED_SAVE_MESSAGE,
    USE_AS_DRAFT_LABEL,
} from "./agentSkillCopy";
import { getAgentSkillErrorMessage } from "./useAgentSkillLifecycle";
import styles from "./AgentSkills.module.scss";

const CHAT_FAILURE_MESSAGE = "Failed to update skill";

interface Props {
    skill: AgentSkill;
    canManage: boolean;
    /** A Details-tab save is in flight; the form would open with stale metadata. */
    isMetadataSaving: boolean;
    onUseDraft: (body: string) => void;
    /** The first edit of a Kick default forks the firm's override: a new row id. */
    onMoved: (skillId: string) => void;
}

interface PendingTurn {
    instruction: string;
    error: string | null;
    rejected: RejectedChatEdit | null;
}

const KickTurn = ({
    text,
    status,
    createdAt,
    children,
}: {
    text: string;
    status: string | null;
    createdAt: Date | null;
    children?: React.ReactNode;
}) => (
    <HStack gap={8} align="start" className={styles.chatKickTurn}>
        <KickAvatar size="xs" aria-hidden />
        <VStack gap={2}>
            <HStack gap={8} align="center">
                <strong>Kick</strong>
                {createdAt && (
                    <span className={styles.chatTime}>
                        {timeAgo(createdAt)}
                    </span>
                )}
            </HStack>
            {text && <span>{text}</span>}
            {status && <span className="text-secondary">{status}</span>}
            {children}
        </VStack>
    </HStack>
);

const ThreadItem = ({ item }: { item: AgentSkillChatItem }) => {
    if (item.kind === "system") {
        const actor = item.actor ?? "";
        return (
            <HStack gap={8} align="center" className={styles.chatAuthorRow}>
                <Avatar name={actor} size="xs" aria-hidden />
                <span>
                    <strong>{actor}</strong> {item.text}
                </span>
                <span className={styles.chatTime}>
                    {timeAgo(item.createdAt)}
                </span>
            </HStack>
        );
    }
    if (item.kind === "user") {
        return <span className={styles.chatUserTurn}>{item.text}</span>;
    }
    return (
        <KickTurn
            text={item.text}
            status={`Updated skill to version ${item.version}`}
            createdAt={item.createdAt}
        />
    );
};

// Kick's draft was not saved: the reasons are listed and the member can take
// the draft into the editor to fix it by hand.
const RejectedTurn = ({
    rejected,
    isMetadataSaving,
    onUseDraft,
}: {
    rejected: RejectedChatEdit;
    isMetadataSaving: boolean;
    onUseDraft: (body: string) => void;
}) => (
    <KickTurn
        text={rejected.summary}
        status={FLAGGED_SAVE_MESSAGE}
        createdAt={null}
    >
        <VStack gap={2} className="text-secondary">
            {describeRejectedChatEdit(rejected).map((line) => (
                <span key={line}>{line}</span>
            ))}
        </VStack>
        <HStack>
            <Button
                variant="secondary"
                size="sm"
                disabled={isMetadataSaving}
                onClick={() => onUseDraft(rejected.proposedBody)}
            >
                {USE_AS_DRAFT_LABEL}
            </Button>
        </HStack>
    </KickTurn>
);

export const AgentSkillChatTab = ({
    skill,
    canManage,
    isMetadataSaving,
    onUseDraft,
    onMoved,
}: Props) => {
    const { data: revisions = [], isFetched } = useAgentSkillRevisions(
        skill.id,
    );
    const { mutateAsync: chatEdit, isPending } =
        useChatEditAgentSkillMutation();
    const toaster = useToaster();
    const [pending, setPending] = useState<PendingTurn | null>(null);
    const threadRef = useRef<HTMLDivElement>(null);
    const thread = buildAgentSkillChatThread(revisions);

    useEffect(() => {
        const element = threadRef.current;
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    }, [thread.length, pending]);

    const send = async (value: SlateValue): Promise<boolean> => {
        const instruction = slateToPlainText(serializeSlateValue(value));
        if (!canManage || isPending || instruction === "") {
            return false;
        }
        if (instruction.length > AGENT_SKILL_CHAT_INSTRUCTION_MAX_CHARS) {
            toaster.error("Instruction is too long");
            return false;
        }
        setPending({ instruction, error: null, rejected: null });
        try {
            const result = await chatEdit({
                skillId: skill.id,
                body: { instruction },
            });
            if (result.outcome === "rejected") {
                setPending({ instruction, error: null, rejected: result });
                return true;
            }
            setPending(null);
            if (result.skill.id !== skill.id) {
                onMoved(result.skill.id);
            }
            return true;
        } catch (error) {
            setPending({
                instruction,
                rejected: null,
                error:
                    error instanceof Error
                        ? getAgentSkillErrorMessage(error, CHAT_FAILURE_MESSAGE)
                        : CHAT_FAILURE_MESSAGE,
            });
            return false;
        }
    };

    return (
        <div className={styles.chatPanel}>
            <div ref={threadRef} className={styles.chatThread}>
                {!isFetched ? (
                    <Loader />
                ) : (
                    <VStack gap={12}>
                        {thread.map((item, index) => (
                            <ThreadItem
                                key={`${item.revisionId}-${item.kind}-${index}`}
                                item={item}
                            />
                        ))}
                        {pending && (
                            <>
                                <span className={styles.chatUserTurn}>
                                    {pending.instruction}
                                </span>
                                {pending.rejected ? (
                                    <RejectedTurn
                                        rejected={pending.rejected}
                                        isMetadataSaving={isMetadataSaving}
                                        onUseDraft={onUseDraft}
                                    />
                                ) : (
                                    <KickTurn
                                        text={pending.error ?? ""}
                                        status={
                                            pending.error ? null : "Updating…"
                                        }
                                        createdAt={null}
                                    />
                                )}
                            </>
                        )}
                    </VStack>
                )}
            </div>
            <Tooltip
                content={
                    canManage ? undefined : AGENT_SKILL_MANAGE_FORBIDDEN_TOOLTIP
                }
            >
                <div className={styles.chatComposer}>
                    <SlateEditor
                        placeholder="Edit skill…"
                        disabled={!canManage || isPending}
                        hideOrganizationScopePicker
                        blurOnEscape
                        onSubmit={send}
                    />
                </div>
            </Tooltip>
        </div>
    );
};
